import React, { Fragment, FC, useState, ChangeEvent } from 'react';
import { Alert, Box, Button, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client';
import { ActionFilter, ActionPriceList, PriceListAction } from 'models/action';
import {
    addActionToPriceListTemplate,
    addDefaultActionPriceListTemplate,
    deleteActionPriceListRowTemplate,
    deleteActionPriceListTemplate,
    editActionPriceListRowTemplate,
    getActionPriceListsTemplate,
    getGroupsTemplate,
    getLabActionsTemplate,
} from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import ActionPriceListsPageActions from './action-price-lists-page-actions';
import AddActionPriceListDialog from './add-action-price-list-dialog/add-action-list-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import ActionPriceListItem from './action-price-list-item.component';
import Loader from 'components/shared/loader/loader.component';
import EditActionPriceListDialog from './edit-action-price-list-dialog/edit-action-list-dialog.component';
import CategoryFilterDropdown from '../shared/category-filter-dropdown/category-filter-dropdown.component';
import GroupFilterDropdown from '../shared/group-filter-dropdown/group-filter-dropdown.component';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { OrderBy } from 'models/search-result';
import { DEFAULT_ACTION_PRICE_LIST_ORDER, sortPriceListActions, toggleOrderBy } from 'utils/price-list-sort';

const ActionPriceLists: FC = () => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'lab-settings', 'orders']);
    const [addListDialogOpen, setAddListDialogOpen] = useState(false);
    const [editListDialogOpen, setEditListDialogOpen] = useState(false);
    const [actionPriceList, setActionPriceList] = useState({} as ActionPriceList);
    const [actions, setActions] = useState<PriceListAction[]>([]);
    const [filters, setFilters] = useState<ActionFilter>({ freetext: '', category: '', group: '' });
    const [orderBy, setOrderBy] = useState<OrderBy>(DEFAULT_ACTION_PRICE_LIST_ORDER);
    const [actionPriceLists, setActionPriceLists] = useState<ActionPriceList[]>([]);
    const [actionPriceListsLoading, setActionPriceListsLoading] = useState(true);
    const { showBoundary } = useDenthubErrorBoundary();
    const [snackbarOpen, setSnackbarOpen] = useState({
        addedPriceList: false,
        updatedPriceList: false,
        deletedPriceList: false,
    });
    const currentOrg = currentOrgVar();

    const isTioTandvardPricingForLabEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING_FOR_LAB,
    );

    const { loading: labActionsLoading } = useQuery(getLabActionsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: { active: true },
        },
        onCompleted({ labActions }) {
            setActions(labActions);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { refetch: refetchActionPriceLists } = useQuery(getActionPriceListsTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted({ actionPriceLists }) {
            console.log('COMPLETED!!');
            setActionPriceLists(actionPriceLists);
            setActionPriceListsLoading(false);
            if (actionPriceList?._id) {
                setActionPriceList(actionPriceLists.find((list: ActionPriceList) => actionPriceList._id === list._id));
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { groups } = {}, loading: groupsLoading } = useQuery(getGroupsTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteActionPriceList, { error: deletePriceListError }] = useMutation(deleteActionPriceListTemplate, {
        variables: {
            priceListId: actionPriceList._id,
        },
        onCompleted: ({ deleteActionPriceList }) => {
            removeActionPriceList(deleteActionPriceList);
        },
        update(cache: ApolloCache<ActionPriceList[]>) {
            cache.modify({
                fields: {
                    actionPriceLists(existingPriceListRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingPriceListRefs instanceof Array) {
                            return [...existingPriceListRefs].filter((priceListRef: Reference | undefined) => {
                                return actionPriceList._id !== readField('_id', priceListRef);
                            });
                        }
                        return [];
                    },
                },
            });
        },
    });

    const [addDefaultActionPriceList] = useMutation(addDefaultActionPriceListTemplate, {
        variables: {
            priceListId: actionPriceList._id,
        },
        onCompleted: ({ addDefaultActionPriceList }) => {
            defaultActionPriceList(addDefaultActionPriceList);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<ActionPriceList>) {
            cache.evict({
                id: 'ROOT_QUERY',
                fieldName: 'actionPriceLists',
            });
            cache.gc();
        },
    });

    const [addActionToPriceList, { loading: addPriceLoading }] = useMutation(addActionToPriceListTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [editActionPriceListRow, { loading: editPriceLoading }] = useMutation(editActionPriceListRowTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteActionPriceListRow] = useMutation(deleteActionPriceListRowTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const handleSelectedPriceList = (selectedPriceList: string) => {
        const lists = [...actionPriceLists];
        const actionPriceList = lists.find(
            (list: ActionPriceList) =>
                list.actionPriceListName.toLocaleLowerCase() === selectedPriceList.toLocaleLowerCase(),
        );
        if (actionPriceList) {
            setActionPriceList(actionPriceList);
        }
    };

    const handleSortChange = (field: string) => {
        setOrderBy(toggleOrderBy(orderBy, field));
    };
    const filteredAndSortedActions = (actions: PriceListAction[]) => {
        let actionsArr = actions.filter(
            ({ description, actionCode, category }: PriceListAction) =>
                (description?.toLocaleLowerCase().includes(filters.freetext.toLocaleLowerCase()) ||
                    actionCode?.toLocaleLowerCase().includes(filters.freetext.toLocaleLowerCase())) &&
                category?.toLocaleLowerCase().includes(filters.category.toLocaleLowerCase()),
        );

        if (filters.group) {
            actionsArr = actionsArr.filter((action) => {
                const groupIds = action.groups.filter(Boolean).map((group) => group._id);
                return groupIds.includes(filters.group);
            });
        }
        return sortPriceListActions(actionsArr, orderBy);
    };
    const getActions = (actionPriceList: ActionPriceList): PriceListAction[] => {
        const actionsArr = [...actions];
        const listActions = actionPriceList.actions?.map((action) => action);
        if (listActions) {
            for (const listAction of listActions) {
                const index = actions.findIndex((action) => action._id === listAction.action);
                actionsArr.splice(index, 1, {
                    ...actions[index],
                    _id: listAction._id,
                    action: listAction.action,
                    basePrice: listAction.basePrice,
                    tiotandvardPrice: listAction.tiotandvardPrice,
                });
            }
            return actionsArr;
        }
        return actionsArr;
    };

    const addActionPriceList = (priceList: ActionPriceList) => {
        setActionPriceList(priceList);
        setSnackbarOpen({ ...snackbarOpen, addedPriceList: true });
    };
    const editActionPriceList = (priceList: ActionPriceList) => {
        setActionPriceList(priceList);
        setSnackbarOpen({ ...snackbarOpen, updatedPriceList: true });
    };
    const removeActionPriceList = (priceListId: string) => {
        const priceLists = [...actionPriceLists];
        const index = priceLists.findIndex((list: ActionPriceList) => list._id?.toString() === priceListId.toString());
        if (index > -1) {
            priceLists.splice(index, 1);
        }
        setActionPriceList({} as ActionPriceList);
        setSnackbarOpen({ ...snackbarOpen, deletedPriceList: true });
    };
    const defaultActionPriceList = (priceList: ActionPriceList) => {
        setActionPriceList(priceList);
        setSnackbarOpen({ ...snackbarOpen, updatedPriceList: true });
    };

    const toFixedNumber = (value?: string | number | null) =>
        value != null ? Number(Number(value).toFixed(2)) : undefined;

    const addActionPrice = async (action: PriceListAction) => {
        await addActionToPriceList({
            variables: {
                priceListId: actionPriceList._id,
                actionId: action._id,
                basePrice: toFixedNumber(action.basePrice),
                tiotandvardPrice: toFixedNumber(action.tiotandvardPrice),
            },
        });
        refetchActionPriceLists();
    };

    const editActionPrice = async (action: PriceListAction) => {
        await editActionPriceListRow({
            variables: {
                priceListId: actionPriceList._id,
                actionId: action._id,
                basePrice: toFixedNumber(action.basePrice),
                tiotandvardPrice: toFixedNumber(action.tiotandvardPrice),
            },
        });
        refetchActionPriceLists();
    };

    const removeActionPrice = async (action: PriceListAction) => {
        await deleteActionPriceListRow({
            variables: {
                priceListId: actionPriceList._id,
                actionId: action._id,
            },
        });
        refetchActionPriceLists();
    };

    const handleFreetextFilterChange = (freetext: string) => {
        setFilters({ ...filters, freetext: freetext });
    };
    const handleCategoryFilterChange = (category: string) => {
        setFilters({ ...filters, category: category });
    };

    const handleGroupFilterChange = (group: string) => {
        setFilters({ ...filters, group: group });
    };

    const handleCloseAddListDialog = () => {
        setAddListDialogOpen(false);
    };

    const getDefaultActionPriceList = () => {
        const defaultPriceList = actionPriceLists.find((list: ActionPriceList) => list.isStandardPriceList);
        if (!defaultPriceList) return;
        setActionPriceList(defaultPriceList);
    };
    if (!actionPriceListsLoading && !actionPriceList._id) {
        getDefaultActionPriceList();
    }

    return (
        <Fragment>
            {actionPriceListsLoading || labActionsLoading || groupsLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    <ActionPriceListsPageActions
                        onNewActionListClick={() => setAddListDialogOpen(true)}
                        actionPriceLists={actionPriceLists || []}
                        actionPriceList={actionPriceList}
                        onSelectedPriceList={handleSelectedPriceList}
                    />
                    {actionPriceList.actionPriceListName && (
                        <Fragment>
                            <Box display="flex" alignItems="center">
                                <Typography
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => setEditListDialogOpen(true)}
                                    variant="h3"
                                >
                                    {actionPriceList.actionPriceListName}
                                </Typography>
                                <Tooltip
                                    title={translate('lab-settings:REMOVE_ACTION_LIST').toString()}
                                    placement="right"
                                >
                                    <IconButton
                                        sx={{
                                            visibility: actionPriceList.isStandardPriceList ? 'hidden' : 'visible',
                                        }}
                                        size="small"
                                        onClick={() => deleteActionPriceList()}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {!actionPriceList.isStandardPriceList && (
                                    <Button sx={{ marginLeft: 1 }} onClick={() => addDefaultActionPriceList()}>
                                        {translate('lab-settings:USE_AS_STANDARD_LIST')}
                                    </Button>
                                )}
                            </Box>
                            {actionPriceList.actions && actions.length >= 1 ? (
                                <Fragment>
                                    <Box mb={1} display="flex" alignItems="center">
                                        <Grid container spacing={1}>
                                            <Grid item sm={4} xs={12}>
                                                <TextField
                                                    fullWidth
                                                    id="filter-name"
                                                    margin="dense"
                                                    name="search"
                                                    label={translate('lab-settings:FILTER_ACTIONS')}
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                        handleFreetextFilterChange(event.target.value)
                                                    }
                                                />
                                            </Grid>
                                            <Grid item sm={4} xs={12}>
                                                <GroupFilterDropdown
                                                    groups={groups}
                                                    group={filters.group}
                                                    onGroupSelect={handleGroupFilterChange}
                                                />
                                            </Grid>
                                            <Grid item sm={4} xs={12}>
                                                <CategoryFilterDropdown
                                                    category={filters.category}
                                                    onCategorySelect={handleCategoryFilterChange}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box mb={2}>
                                        <Paper variant="outlined">
                                            <ActionPriceListItem
                                                actions={filteredAndSortedActions(getActions(actionPriceList))}
                                                onSaveActionPrice={addActionPrice}
                                                onEditActionPrice={editActionPrice}
                                                onRemoveActionPrice={removeActionPrice}
                                                currency={actionPriceList.currency || 'SEK'}
                                                addPriceLoading={addPriceLoading}
                                                editPriceLoading={editPriceLoading}
                                                isTioTandvardPricingForLabEnabled={isTioTandvardPricingForLabEnabled}
                                                orderBy={orderBy}
                                                onSortChange={handleSortChange}
                                            />
                                        </Paper>
                                    </Box>
                                </Fragment>
                            ) : (
                                <Box padding={2}>
                                    <Alert severity="info">{translate('lab-settings:NO_ACTIVE_ACTIONS')}</Alert>
                                </Box>
                            )}
                        </Fragment>
                    )}
                </Fragment>
            )}
            <AddActionPriceListDialog
                dialogOpen={addListDialogOpen}
                onCloseDialog={handleCloseAddListDialog}
                onAddActionPriceList={addActionPriceList}
                actionPriceLists={actionPriceLists}
            />
            <EditActionPriceListDialog
                actionPriceList={actionPriceList}
                dialogOpen={editListDialogOpen}
                onCloseDialog={() => setEditListDialogOpen(false)}
                onEditActionPriceList={editActionPriceList}
                actionPriceLists={actionPriceLists}
            />
            {snackbarOpen.addedPriceList ? (
                <AlertSnackbar type="success" message={translate('lab-settings:PRICE_LIST_ADDED')} />
            ) : undefined}
            {snackbarOpen.updatedPriceList ? (
                <AlertSnackbar type="success" message={translate('lab-settings:PRICE_LIST_UPDATED')} />
            ) : undefined}
            {snackbarOpen.deletedPriceList ? (
                <AlertSnackbar type="success" message={translate('lab-settings:PRICE_LIST_DELETED')} />
            ) : undefined}
            {deletePriceListError ? <AlertSnackbar type="error" message={deletePriceListError?.message} /> : undefined}
        </Fragment>
    );
};

export default ActionPriceLists;
